import { type ReactNode, useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { Creator, CreatorStatus } from '@/types';
import { cn } from '@/utils/cn';
import { PIPELINE_COLUMNS } from '../types';
import { useMoveCreator } from '../hooks/use-move-creator';
import { KanbanColumn } from './kanban-column';
import { KanbanCardOverlay } from './kanban-card-overlay';
import { useSkeletonCounts } from './kanban-skeleton';

type Props = { onSelect: (id: string) => void };

function useDragHandlers() {
  const [active, setActive] = useState<Creator | null>(null);
  const move = useMoveCreator();
  const onStart = useCallback(
    (e: DragStartEvent) =>
      setActive((e.active.data.current as { creator: Creator }).creator),
    []
  );
  const onEnd = useCallback(
    (e: DragEndEvent) => {
      const d = active;
      setActive(null);
      if (!e.over || !d) return;
      const to = e.over.id as CreatorStatus;
      if (d.status !== to)
        move.mutate({ id: d.id, from: d.status, to, creator: d });
    },
    [active, move]
  );
  return { active, onStart, onEnd, onCancel: () => setActive(null) };
}

function Columns({
  counts,
  onSelect,
}: {
  counts: number[];
  onSelect: (id: string) => void;
}): ReactNode {
  return (
    <>
      {PIPELINE_COLUMNS.map((c, i) => (
        <KanbanColumn
          key={c.status}
          {...c}
          skeletonCount={counts[i] ?? 5}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

const BOARD =
  'flex h-full gap-sm overflow-x-auto pb-sm scrollbar-thin snap-x snap-mandatory sm:snap-none';

export function KanbanBoard({ onSelect }: Props): ReactNode {
  const { active, onStart, onEnd, onCancel } = useDragHandlers();
  const counts = useSkeletonCounts();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  return (
    <DndContext
      sensors={sensors}
      onDragStart={onStart}
      onDragEnd={onEnd}
      onDragCancel={onCancel}
    >
      <div className={cn(BOARD, active && 'cursor-grabbing')}>
        <Columns counts={counts} onSelect={onSelect} />
      </div>
      <DragOverlay dropAnimation={null}>
        {active && <KanbanCardOverlay creator={active} />}
      </DragOverlay>
    </DndContext>
  );
}
